import { useState, useEffect } from 'react';
import Loader from '../../../components/globalloaders/Loader';
import { useLoading } from '../../../components/globalloaders/LoadingContext';
import { toast } from 'react-toastify';
import { Bars3Icon, EllipsisVerticalIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { countriesOptions } from '../../../constants/options';
import { formatLabel, capitalizeWords, isValidPhone, encryptPayload, decryptPayload, splitPhone, formatTableDateTime, formatDateTime, formatCurrency } from '../../../constants/helpers';
import ConfirmDialog from '../../../components/dialogbox/ConfirmDialog';
import OpCodeDialog from "../../../components/dialogbox/OpCodeDialog";
import FallBack from '../../../components/dialogbox/FallBack';
import api from '../../../utils/auth/api';

const Client = ({ setIsMobileOpen }) => {

  const { isLoading, setIsLoading } = useLoading();

  const [loaders, setLoaders] = useState({
    fetch: false,
    update: false,
    details: false,
    search: false,
  });
  const [dialog, setDialog] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
    confirmText: "",
    action: () => { },
  });
  const [opCodeDialog, setOpCodeDialog] = useState({
    open: false,
    id: null,
  });

  const [clientData, setClientData] = useState([]);

  // Menu List State
  const [menuState, setMenuState] = useState({
    open: false,
    rowId: null,
    rowData: null,
    position: { top: 0, left: 0 },
  });
  const openMenu = ({ id, position, row }) => {
    setMenuState({
      open: true,
      rowId: id,
      rowData: row,
      position,
    });
  };
  const closeMenu = () => {
    setMenuState({
      open: false,
      rowId: null,
      rowData: null,
      position: { top: 0, left: 0 },
    });
  };

  // For Search Quick & Deep
  const [quickResults, setQuickResults] = useState([]);
  const [deepResults, setDeepResults] = useState([]);
  const [query, setQuery] = useState("");
  const [searchMode, setSearchMode] = useState("quick");
  const [isQuickSearching, setIsQuickSearching] = useState(false);
  const [totalBackendPg, setTotalBackendPg] = useState(1);
  const getDisplayData = () => {
    if (searchMode === "quick") {
      return isQuickSearching ? quickResults : clientData;
    }
    if (searchMode === "deep") {
      return deepResults;
    }
    return clientData;
  };

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const displayData = getDisplayData();
  const [totalPages, setTotalPages] = useState(1);
  const paginatedData = displayData;
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalBackendPg);
  useEffect(() => {
    setQuery("");
    setQuickResults([]);
    setDeepResults([]);
    setIsQuickSearching(false);
    if (searchMode === "quick") {
      setCurrentPage(1);
      setTotalPages(Math.ceil(totalBackendPg / itemsPerPage));
    } else if (searchMode === "deep") {
      setCurrentPage(1);
      setTotalPages(1);
    }
  }, [searchMode, totalBackendPg, itemsPerPage]);

  // Details DialogBox with Api Call By ID 
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [formData1, setFormData1] = useState({});
  const [openAgreementId, setOpenAgreementId] = useState(null);
  const toggleAgreement = (id) => {
    setOpenAgreementId((prev) => (prev === id ? null : id));
  };
  const handleViewOpen = async (id) => {
    setIsViewOpen(true);
    setLoaders(prev => ({ ...prev, details: true }));
    try {
      const response = await api.get(`${process.env.REACT_APP_URI}/client/detail/${id}`);
      if (response.data.success) {
        const decryptedData = decryptPayload(response.data.encryptedData);
        setFormData1(decryptedData);
      }
      else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching Client details:', error);
    } finally {
      setLoaders(prev => ({ ...prev, details: false }));
      return;
    }
  };
  const handleViewClose = () => {
    setIsViewOpen(false);
    setOpenAgreementId(null);
    setFormData1({});
  };

  // Update DialogBox with Api Call and Send
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const requiredFields = [
    { key: "name", label: "Client Name" },
    { key: "countryCode", label: "Client Country Code" },
    { key: "phone", label: "Client Phone" },
    { key: "address", label: "Client Address" },
  ];
  const [formData2, setFormData2] = useState({
    id: "",
    name: "",
    countryCode: "PK",
    phone: "",
    address: "",
  });
  const handleChangeUpdate = (e) => {
    const { name, value } = e.target;
    const autoCapFields = ["name", "address"];
    setFormData2({
      ...formData2,
      [name]: autoCapFields.includes(name) ? capitalizeWords(value) : value,
    });
  };
  const handleUpdateOpen = async (id) => {
    setIsUpdateOpen(true);
    setLoaders(prev => ({ ...prev, update: true }));
    try {
      const response = await api.get(`${process.env.REACT_APP_URI}/client/detail/${id}`);
      if (response.data.success) {
        const decryptedData = decryptPayload(response.data.encryptedData);
        const { countryCode, phone } = splitPhone(decryptedData?.phone);
        setFormData2({
          id,
          name: decryptedData?.name || '',
          countryCode: countryCode || '',
          phone: phone || '',
          address: decryptedData?.address || '',
        });
      }
      else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching Client edit:', error);
    } finally {
      setLoaders(prev => ({ ...prev, update: false }));
      return;
    }
  };
  const handleUpdateClose = () => {
    setIsUpdateOpen(false);
    setFormData2({
      id: "",
      name: "",
      countryCode: "PK",
      phone: "",
      address: "",
    });
  };
  // Update API
  const handleUpdate = async (id) => {

    if (isLoading) return;

    for (let field of requiredFields) {
      if (!formData2[field.key] || formData2[field.key].toString().trim() === "") {
        toast.error(`${field.label} is required`);
        return;
      }
    };

    if (!id) {
      toast.error('Unable to update: ID is missing');
      return;
    };

    const payload = {
      name: formData2.name,
      countryCode: formData2.countryCode,
      phone: formData2.phone,
      address: formData2.address,
    };

    setIsLoading(true);

    const encryptedData = encryptPayload(payload);
    try {
      const response = await api.put(`${process.env.REACT_APP_URI}/client/update/${id}`, { data: encryptedData });
      if (response.data.success) {
        const decryptedData = decryptPayload(response.data.encryptedData);
        setClientData((prevClientData) => prevClientData.map((client) => client._id === id ? { ...client, ...decryptedData } : client));
        toast.success(response.data.message);
        handleUpdateClose();
      } else {
        toast.error(response.data.message);
      }
    }
    catch (error) {
      console.log("Client Updating Data failed:", error);
      if (error.response && error.response.data) {
        const data = error.response.data;
        if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          data.errors.forEach(err => toast.warning(err));
        } else {
          toast.error(data.message);
        }
      } else {
        toast.error("Unable to Update Client Network Error, please try again!");
      }
    }
    finally {
      setIsLoading(false);
      return;
    }
  };

  // QUICK SEARCH (local state)
  const handleQuickSearch = (e) => {
    const value = e.target.value;
    setQuery(value);
    if (searchMode === "quick") {
      if (value.trim() === "") {
        setIsQuickSearching(false);
        setQuickResults(clientData);
      } else {
        setIsQuickSearching(true);
        const results = clientData.filter((s) =>
          s.name.toLowerCase().includes(value.toLowerCase()) ||
          s.phone?.toLowerCase().includes(value.toLowerCase()) ||
          s.clientId?.toLowerCase().includes(value.toLowerCase())
        );
        setQuickResults(results);
      }
    }
  };
  // DEEP SEARCH (API)
  const handleDeepSearch = async (searchQuery) => {
    if (!searchQuery.trim()) {
      toast.warning("Enter value to search");
      return;
    }
    setLoaders(prev => ({ ...prev, search: true }));
    try {
      const response = await api.get(`${process.env.REACT_APP_URI}/client/search`, {
        params: { value: searchQuery },
      });
      if (response.data.success) {
        const decryptedData = decryptPayload(response.data.encryptedData);
        setDeepResults(decryptedData);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error deep searching client:", error);
      toast.error(error?.response.data.message || "Deep search client failed!");
    } finally {
      setLoaders(prev => ({ ...prev, search: false }));
      return;
    }
  };
  // Connect Search to Quick or Deep via searchMode
  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    if (searchMode === "quick") {
      handleQuickSearch({ target: { value: query } });
    } else if (searchMode === "deep") {
      handleDeepSearch(query);
    }
  };

  // Fetch All API
  useEffect(() => {
    const fetchData = async (page = 1) => {
      setLoaders(prev => ({ ...prev, fetch: true }));
      try {
        const response = await api.get(`${process.env.REACT_APP_URI}/client/get-all`, {
          params: { page, limit: itemsPerPage },
        });
        if (response.data.success) {
          const decryptedData = decryptPayload(response.data.encryptedData);
          setClientData(decryptedData.data);
          setQuickResults(decryptedData.data);
          setTotalBackendPg(decryptedData.total);
          setTotalPages(Math.ceil(decryptedData.total / itemsPerPage));
        }
        else {
          toast.error(response.data.message);
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
      } finally {
        setLoaders(prev => ({ ...prev, fetch: false }));
        return;
      }
    };
    fetchData(currentPage);
  }, [currentPage]);

  // Delete API By ID
  const handleDelete = async (id, opcode) => {
    if (isLoading) return;
    setIsLoading(true);
    const payload = encryptPayload({ id, opcode });
    try {
      const response = await api.delete(`${process.env.REACT_APP_URI}/client/delete`, { data: { data: payload } });
      if (response.data.success) {
        setClientData((prevClientData) => prevClientData.filter((client) => client._id !== id));
        toast.success(response.data.message);
      }
    }
    catch (error) {
      console.log("Error occur while deleting client:", error);
      if (error.response && error.response.data) {
        const data = error.response.data;
        toast.error(data.message);
      } else {
        toast.error("Unable to Delete Client Network Error, please try again!");
      }
    }
    finally {
      setIsLoading(false);
      return;
    }
  };

  // Add DialogBox Code & Store API
  const initialFormState = {
    name: '',
    countryCode: "PK",
    phone: '',
    address: '',
  };
  const [formData, setFormData] = useState(initialFormState);
  const [isAddDialog, setAddDialog] = useState(false);
  const handleAddOpen = () => {
    setAddDialog(true);
  };
  const handleAddClose = () => {
    setAddDialog(false);
    setFormData(initialFormState);
  };
  const onChange = (e) => {
    const { name, value } = e.target;
    const autoCapFields = ["name", "address"];
    setFormData({
      ...formData,
      [name]: autoCapFields.includes(name) ? capitalizeWords(value) : value,
    });
  };
  const handleSubmit = async () => {

    if (isLoading) return;

    // --- Required field checks (city excluded) ---
    if (!formData.name) {
      toast.warning("Client Name is required.");
      return;
    }
    if (!formData.phone || !isValidPhone(formData.phone, formData.countryCode)) {
      toast.warning("Client Phone is missing or invalid.");
      return;
    }
    if (!formData.address) {
      toast.warning("Client Address is required.");
      return;
    }

    const encryptedData = encryptPayload(formData);

    setIsLoading(true);
    try {
      const response = await api.post(`${process.env.REACT_APP_URI}/client/create`, { data: encryptedData });
      if (response.data.success) {
        toast.success(response.data.message);
        const decryptedData = decryptPayload(response.data.encryptedData);
        setClientData((prevData) => [decryptedData, ...prevData]);
        handleAddClose();
      }
      else {
        toast.error(response.data.message);
      }
    }
    catch (error) {
      console.log("Client Creation failed:", error);
      if (error.response && error.response.data) {
        const data = error.response.data;
        if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          data.errors.forEach(err => toast.warning(err));
        } else {
          toast.error(data.message);
        }
      } else {
        toast.error("Unable to Create Client Network Error, please try again!");
      }
    }
    finally {
      setIsLoading(false);
      return;
    }
  };

  // Fallback control state
  const hasViewData = Object.keys(formData1 || {}).length > 0;
  const hasUpdateData = formData2?.id;

  // Close menu list on outside click
  useEffect(() => {
    if (!menuState.open) return;
    const handleOutside = (e) => {
      const isTrigger = e.target.closest('[data-menu-trigger="true"]');
      const isMenu = e.target.closest('[data-floating-menu="true"]');
      if (isTrigger || isMenu) return;
      closeMenu();
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", closeMenu);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", closeMenu);
    };
  }, [menuState.open]);

  // actions btn list
  const menuActionList = [
    {
      label: "Edit", type: "edit", color: "text-blue-600", hover: "hover:bg-blue-100",
      boxType: "info", message: "Do you want to edit this client details?",
    },
    {
      label: "Delete", type: "delete", color: "text-red-600", hover: "hover:bg-red-100",
      boxType: "danger",
      message: "This will permanently delete the client, all agreements with this client, and all related entries for each agreement. This action cannot be undone.",
    },
  ];

  return (
    <>
      <ConfirmDialog
        isOpen={dialog.open}
        onClose={() => setDialog((d) => ({ ...d, open: false }))}
        onConfirm={dialog.action}
        title={dialog.title}
        message={dialog.message}
        confirmText={dialog.confirmText}
        type={dialog.type}
      />
      <OpCodeDialog
        isOpen={opCodeDialog.open}
        type="danger"
        title="Process Authorization"
        message="By proceeding, you accept responsibility for any resulting data loss or modification. Please enter the OpCode to continue."
        confirmText="Verify"
        onClose={() =>
          setOpCodeDialog({ open: false, id: null })
        }
        onConfirm={(code) => {
          handleDelete(opCodeDialog.id, code);
        }}
      />
      {/* Three Dot Menu UI */}
      {menuState.open && (
        <div className="fixed inset-0 z-50 pointer-events-none">
          <div
            data-floating-menu="true"
            className="absolute bg-white border shadow-xl rounded-xl p-2 w-44 space-y-1 pointer-events-auto"
            style={{
              top: menuState.position.top,
              left: menuState.position.left,
            }}
          >
            {menuActionList.map((action) => (
              <button
                key={action.type}
                className={`flex items-center w-full gap-2 p-2 rounded-lg ${action.hover} text-sm font-medium cursor-pointer`}
                onClick={() => {
                  closeMenu();
                  if (action.type === "edit") {
                    setDialog({
                      open: true,
                      type: action.boxType,
                      title: "Edit Client",
                      message: action.message,
                      confirmText: action.label,
                      action: () => handleUpdateOpen(menuState.rowId),
                    });
                    return;
                  }
                  setDialog({
                    open: true,
                    type: action.boxType,
                    title: "Delete Client",
                    message: action.message,
                    confirmText: "Proceed",
                    action: () => {
                      setOpCodeDialog({
                        open: true,
                        id: menuState.rowId,
                      });
                    },
                  });
                }}
              >
                <span className={action.color}>{action.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Details & Update UI */}
      {isViewOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-[2px] z-40 p-3">
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto animate-fadeIn border border-gray-200">
            <div className="max-h-[85vh] flex flex-col">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-3 sm:p-4 shadow-md text-center">
                <h2 className="text-xl sm:text-2xl font-bold">Client Details</h2>
                <p className="text-[10px] sm:text-xs opacity-80">Smart by Logic. Built for You.</p>
              </div>
              {loaders.details ? (
                <div className="flex justify-center items-center h-[45vh]">
                  <Loader />
                </div>
              ) : !hasViewData ? (
                <FallBack
                  title="Client details not available"
                  message="This client record could not be loaded. It may have been removed, or there was a network issue."
                  actionText="Close"
                  onAction={handleViewClose}
                />
              ) : (
                <div className="flex-1 space-y-4 overflow-auto p-3 bg-gray-50">
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center text-xl font-bold text-indigo-700">
                        {formData1?.name?.charAt(0) || "C"}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">
                          {formData1?.name}
                        </h3>
                        <p className="text-sm text-gray-500">Client</p>
                      </div>
                    </div>

                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">ID</span>
                        <span className="font-medium text-gray-800">
                          {formData1?.clientId}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-gray-500">Phone</span>
                        <span className="font-medium text-gray-800">
                          {formData1?.phone}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-gray-500">Address</span>
                        <span className="font-medium text-gray-800 text-right max-w-[65%]">
                          {formData1?.address}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-gray-500">Agreements</span>
                        <span className="font-medium text-gray-800 text-right max-w-[65%]">
                          {formData1?.agreementRef?.length ?? 0}
                        </span>
                      </div>
                    </div>
                  </div>
                  {formData1?.agreements?.map((agreement, index) => (
                    <div key={agreement._id} className="space-y-2">
                      <div
                        onClick={() => toggleAgreement(agreement._id)}
                        className={`flex justify-between items-center cursor-pointer p-4 sm:p-6 rounded-2xl shadow-sm border transition-colors duration-300
                          ${openAgreementId === agreement._id
                            ? "bg-indigo-50 border-indigo-300 border-2 shadow-inner"
                            : "bg-white border-gray-200"}`
                        }
                      >
                        <div>
                          <div className="flex justify-between items-center gap-2 flex-wrap">
                            <h2 className="text-md sm:text-lg font-bold text-gray-800">
                              #{index + 1} - {formatLabel(agreement?.category)}
                            </h2>
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full
                                ${agreement?.status === "pending"
                                  ? "bg-blue-100 text-blue-700"
                                  : agreement?.status === "active"
                                    ? "bg-green-100 text-green-700"
                                    : agreement?.status === "expired"
                                      ? "bg-red-100 text-red-700"
                                      : "bg-gray-100 text-gray-600"
                                }`}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full
                                  ${agreement?.status === "pending"
                                    ? "bg-blue-600"
                                    : agreement?.status === "active"
                                      ? "bg-green-600"
                                      : agreement?.status === "expired"
                                        ? "bg-red-600"
                                        : "bg-gray-500"
                                  }`}
                              />
                              {formatLabel(agreement?.status)}
                            </span>
                          </div>
                          <p className="text-gray-500 text-sm">Created: {formatDateTime(agreement?.createdAt)}</p>
                        </div>
                        <ChevronDownIcon
                          className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${openAgreementId === agreement._id ? "rotate-180" : "rotate-0"
                            }`}
                        />
                      </div>
                      {openAgreementId === agreement._id && (
                        <div className="bg-indigo-50 border-indigo-300 border-2 rounded-2xl shadow-inner p-1 space-y-3 transition-colors duration-300">
                          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-10 gap-y-6 text-sm">
                              <div>
                                <h2 className="text-lg sm:text-xl font-bold text-gray-800">
                                  ID: {agreement?.agreementId}
                                </h2>
                              </div>

                              <div>
                                <p className="text-gray-500">Category</p>
                                <p className="font-medium text-gray-800">{formatLabel(agreement?.category)}</p>
                              </div>

                              <div>
                                <p className="text-gray-500">Ref No</p>
                                <p className="font-medium text-gray-800">{agreement?.refNo}</p>
                              </div>

                              <div>
                                <p className="text-gray-500">Valid For</p>
                                <p className="font-medium text-gray-800">{formatLabel(agreement?.validFor)}</p>
                              </div>

                              <div>
                                <p className="text-gray-500">Agreement Date</p>
                                <p className="font-medium text-gray-800">
                                  {formatDateTime(agreement?.agreementDate)}
                                </p>
                              </div>

                              <div>
                                <p className="text-gray-500">Expiry Date</p>
                                <p className="font-medium text-gray-800">
                                  {formatDateTime(agreement?.expiryDate)}
                                </p>
                              </div>

                              <div>
                                <p className="text-gray-500">Client Name</p>
                                <p className="font-medium text-gray-800">{formatLabel(agreement?.clientName)}</p>
                              </div>

                              <div>
                                <p className="text-gray-500">Client Address</p>
                                <p className="font-medium text-gray-800">{formatLabel(agreement?.clientAddress)}</p>
                              </div>

                              <div>
                                <p className="text-gray-500">Client Phone</p>
                                <p className="font-medium text-gray-800">{agreement?.clientPhone}</p>
                              </div>

                              <div>
                                <p className="text-gray-500">Salary</p>
                                <p className="font-medium text-gray-800">PKR {formatCurrency(agreement?.salary)}</p>
                              </div>

                              <div>
                                <p className="text-gray-500">Commission</p>
                                <p className="font-medium text-gray-800">PKR {formatCurrency(agreement?.commission)}</p>
                              </div>

                              <div>
                                <p className="text-gray-500">Status</p>
                                <span
                                  className={`inline-flex items-center px-3 py-1 mt-1 text-xs font-medium rounded-full w-fit
                                              ${agreement?.status === "pending" && "bg-blue-100 text-blue-700"}
                                              ${agreement?.status === "active" && "bg-green-100 text-green-700"}
                                              ${agreement?.status === "expired" && "bg-red-100 text-red-700"}
                                        `}>
                                  {formatLabel(agreement?.status)}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6">
                            <div className="flex items-center gap-4 mb-6">
                              <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center text-xl font-bold text-blue-700">
                                {agreement?.employee?.name?.charAt(0) || "E"}
                              </div>
                              <div>
                                <h3 className="text-lg font-semibold text-gray-800">
                                  {agreement?.employee?.name}
                                </h3>
                                <p className="text-sm text-gray-500">Employee - Main</p>
                              </div>
                            </div>

                            <div className="space-y-3 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-500">ID</span>
                                <span className="font-medium text-gray-800">
                                  {agreement?.employee?.employeeId}
                                </span>
                              </div>

                              <div className="flex justify-between">
                                <span className="text-gray-500">F/H</span>
                                <span className="font-medium text-gray-800">
                                  {agreement?.employee?.father_husband_name}
                                </span>
                              </div>

                              <div className="flex justify-between">
                                <span className="text-gray-500">Phone</span>
                                <span className="font-medium text-gray-800">
                                  {agreement?.employee?.phone}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-3">
                            <h3 className="text-lg font-semibold text-gray-800 mb-3">Timeline / Entries</h3>
                            {agreement?.entries?.map((entry) => (
                              <div key={entry._id} className="mb-3 relative">
                                <div className="bg-gray-50 rounded-xl border border-gray-200 p-3 shadow-sm">
                                  {/* Entry Header */}
                                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-4">
                                    <div>
                                      <h4 className="text-sm sm:text-base font-semibold text-gray-800">
                                        ID: {entry.entryId} - {formatLabel(entry.category)}
                                      </h4>
                                      <p className="text-gray-500 text-xs sm:text-sm">
                                        {formatDateTime(entry.date)} | Type: {formatLabel(entry.type)} | Placement: {entry.placementIndex}
                                      </p>
                                    </div>
                                    <span className={`inline-flex items-center px-3 py-1 mt-1 text-xs font-medium rounded-full w-fit
                                                      ${entry.status === "pending" ? "bg-blue-100 text-blue-700" : ""}
                                                      ${entry.status === "approved" ? "bg-green-100 text-green-700" : ""}
                                            `}>
                                      {formatLabel(entry.status)}
                                    </span>
                                  </div>
                                  {/* Employee Info */}
                                  <div className="flex items-center gap-3 mt-4">
                                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-700 text-sm">
                                      {entry.employee?.name?.charAt(0) || "E"}
                                    </div>
                                    <div className="flex-1 text-sm sm:text-base">
                                      <p className="font-semibold text-gray-800">{entry.employee?.name}</p>
                                      <p className="text-gray-500 text-xs sm:text-sm">F/H: {entry.employee?.father_husband_name}</p>
                                      <p className="text-gray-500 text-xs sm:text-sm">Phone: {entry.employee?.phone}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {hasViewData && (
                <div className="border-t p-2.5 sm:px-4 sm:py-2.5 flex items-center justify-between">
                  <span className='text-xs'>
                    <span className="font-medium text-gray-600">Updated At:</span>{" "}
                    {formatTableDateTime(formData1.updatedAt)}
                  </span>
                  <button
                    type='button'
                    onClick={() => handleViewClose()}
                    className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base bg-blue-600 hover:bg-blue-500 text-white font-semibold transition"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {isUpdateOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-[2px] z-40 p-3">
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto animate-fadeIn border border-gray-200">
            <div className="max-h-[85vh] flex flex-col">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-3 sm:p-4 shadow-md text-center">
                <h2 className="text-xl sm:text-2xl font-bold">Update Client</h2>
                <p className="text-[10px] sm:text-xs opacity-80">Smart by Logic. Built for You.</p>
              </div>
              {loaders.update ? (
                <div className="flex justify-center items-center h-[45vh]">
                  <Loader />
                </div>
              ) : !hasUpdateData ? (
                <FallBack
                  title="Unable to load client for update"
                  message="The client data could not be loaded for editing. Please refresh or try again."
                  actionText="Close"
                  onAction={handleUpdateClose}
                />
              ) : (
                <div className="flex-1 overflow-auto p-2.5 sm:p-5 space-y-6 bg-gray-50">
                  <fieldset className="bg-white border p-3.5 rounded-lg">
                    <legend className="text-md sm:text-lg font-semibold text-gray-700 px-2">Client Info</legend>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Name</label>
                        <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                          placeholder="John Doe"
                          type="text"
                          name='name'
                          value={formData2.name}
                          onChange={handleChangeUpdate}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                        <div className="flex items-center rounded-lg border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-blue-400 transition">
                          <select
                            value={formData2.countryCode}
                            onChange={(e) =>
                              setFormData2({
                                ...formData2,
                                countryCode: e.target.value,
                                phone: "",
                              })
                            }
                            className="w-[88px] shrink-0 px-2 py-2 text-sm bg-transparent border-r border-gray-200 focus:outline-none cursor-pointer"
                          >
                            {countriesOptions.map((c) => (
                              <option key={c.code} value={c.code}>
                                {c.code} {c.dial}
                              </option>
                            ))}
                          </select>
                          <input
                            type="tel"
                            inputMode="numeric"
                            placeholder="Phone number"
                            value={formData2.phone}
                            onChange={(e) => {
                              const digits = e.target.value.replace(/\D/g, "");
                              const selectedCountry = countriesOptions.find(
                                (c) => c.code === formData2.countryCode
                              );
                              const maxLength = selectedCountry?.maxLength || 15;
                              setFormData2({
                                ...formData2,
                                phone: digits.slice(0, maxLength),
                              });
                            }}
                            className="flex-1 px-3 py-2 outline-none bg-transparent text-sm"
                          />
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          Include number only — country code is selected separately
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Address</label>
                        <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                          placeholder='House# 1, 123 Main Street, Gulistan Jauhar'
                          type="text"
                          name='address'
                          value={formData2.address}
                          onChange={handleChangeUpdate}
                        />
                      </div>
                    </div>
                  </fieldset>
                </div>
              )}
              {!loaders.update && hasUpdateData && (
                <div className="border-t p-2.5 sm:px-4 sm:py-2.5 flex justify-between">
                  <button
                    type='button'
                    onClick={handleUpdateClose}
                    className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base text-gray-600 bg-gray-200 hover:bg-gray-300 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type='button'
                    onClick={() => setDialog({
                      open: true,
                      title: "Confirm Update",
                      message: "Are you sure you want to update this client’s information? Changes will be saved immediately.",
                      confirmText: "Update",
                      type: "warning",
                      action: () => handleUpdate(formData2.id),
                    })}
                    className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base bg-blue-600 text-white hover:bg-blue-700 transition"
                  >
                    Update
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="w-full">
        {/* Heading */}
        <div className="w-full mb-5 flex flex-wrap items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <button
              className="md:hidden p-2 bg-white shadow rounded-lg"
              onClick={() => setIsMobileOpen(true)}
            >
              <Bars3Icon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-800" />
            </button>
            <div className="flex flex-wrap items-center">
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-wide text-blue-600">
                Cli
              </h1>
              <span className="text-2xl sm:text-3xl font-extrabold tracking-wide text-gray-800">
                ent
              </span>
            </div>
          </div>

          <div>
            <button
              className="px-4 sm:px-6 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-500 font-medium text-sm sm:text-base"
              onClick={handleAddOpen}
            >
              Add
            </button>
            {isAddDialog && (
              <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-[2px] z-40 p-3">
                <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto animate-fadeIn border border-gray-200">
                  <div className="max-h-[85vh] flex flex-col">
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-3 sm:p-4 shadow-md text-center">
                      <h2 className="text-xl sm:text-2xl font-bold">Create Client</h2>
                      <p className="text-[10px] sm:text-xs opacity-80">Smart by Logic. Built for You.</p>
                    </div>
                    <div className="flex-1 overflow-auto p-2.5 sm:p-5 bg-gray-50">
                      <fieldset className="bg-white border p-3.5 rounded-lg">
                        <legend className="text-md sm:text-lg font-semibold text-gray-700 px-2">Client Info</legend>
                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Name</label>
                            <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                              placeholder="Full Name"
                              type="text"
                              name='name'
                              value={formData.name}
                              onChange={onChange}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                            <div className="flex items-center rounded-lg border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-blue-400 transition">
                              <select
                                value={formData.countryCode}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    countryCode: e.target.value,
                                    phone: "",
                                  })
                                }
                                className="w-[88px] shrink-0 px-2 py-2 text-sm bg-transparent border-r border-gray-200 focus:outline-none cursor-pointer"
                              >
                                {countriesOptions.map((c) => (
                                  <option key={c.code} value={c.code}>
                                    {c.code} {c.dial}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="tel"
                                inputMode="numeric"
                                placeholder="Phone number"
                                value={formData.phone}
                                onChange={(e) => {
                                  const digits = e.target.value.replace(/\D/g, "");
                                  const selectedCountry = countriesOptions.find(
                                    (c) => c.code === formData.countryCode
                                  );
                                  const maxLength = selectedCountry?.maxLength || 15;
                                  setFormData({
                                    ...formData,
                                    phone: digits.slice(0, maxLength),
                                  });
                                }}
                                className="flex-1 px-3 py-2 outline-none bg-transparent text-sm"
                              />
                            </div>
                            <p className="text-xs text-gray-400 mt-1">
                              Include number only — country code is selected separately
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700">Address</label>
                            <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                              placeholder="H#01, ST#01, ABC..."
                              type="text"
                              name='address'
                              value={formData.address}
                              onChange={onChange}
                            />
                          </div>
                        </div>
                      </fieldset>
                    </div>
                    <div className="border-t p-2.5 sm:px-4 sm:py-2.5 flex justify-between">
                      <button
                        type='button'
                        onClick={() => handleAddClose()}
                        className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 transition"
                      >
                        Close
                      </button>
                      <button
                        type="button"
                        onClick={handleSubmit}
                        className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base bg-blue-600 text-white hover:bg-blue-700 transition"
                      >
                        Create
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <hr className="border-t border-gray-200 my-4" />

        {/* Data Display Code */}
        <div className='w-full'>
          {/* Quick & Deep Search Bar */}
          <div className="w-full flex justify-center mb-7">
            <form
              onSubmit={handleSearchSubmit}
              className="relative w-full max-w-2xl flex items-center"
            >
              {/* Search Input */}
              <div className="relative flex-1">
                <input
                  type="search"
                  placeholder={`${searchMode === "quick" ? "Quick" : "Deep"} search . . .`}
                  value={query}
                  onChange={handleQuickSearch}
                  className="w-full py-3 pl-10 sm:pl-12 pr-32 sm:pr-40 rounded-2xl bg-white/80 backdrop-blur-sm 
                             border border-gray-200 shadow-md focus:ring-2 focus:ring-indigo-400 
                             focus:outline-none text-gray-700 placeholder-gray-400 transition"
                />
                <MagnifyingGlassIcon className="absolute w-5 h-5 left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
              {/* Mode Toggle */}
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex rounded-full overflow-hidden border border-gray-200 shadow">
                <button
                  type="button"
                  onClick={() => setSearchMode("quick")}
                  className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium transition-all duration-150 ${searchMode === "quick"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                    }`}
                >
                  Quick
                </button>
                <button
                  type="button"
                  onClick={() => setSearchMode("deep")}
                  className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium transition-all duration-150 ${searchMode === "deep"
                    ? "bg-indigo-600 text-white"
                    : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                    }`}
                >
                  Deep
                </button>
              </div>
            </form>
          </div>

          {/* Table Container */}
          <div className="overflow-x-auto h-[70vh]">
            {(loaders.fetch || loaders.search) ? (
              <div className="flex justify-center items-center h-full p-6">
                <Loader />
              </div>
            ) : displayData.length === 0 ? (
              <div className="flex justify-center items-center h-full p-6">
                <p className="font-semibold text-gray-600 text-xl sm:text-md">
                  {searchMode === "quick" && query.trim()
                    ? "No Match !!"
                    : searchMode === "deep"
                      ? "No Records !!"
                      : "No Client !!"}
                </p>
              </div>
            ) : (
              <>
                <table className="w-full bg-white">
                  <thead className="bg-gray-50">
                    <tr>
                      {[
                        "#",
                        "ID",
                        "Name",
                        "Phone",
                        "Created",
                        "Action",
                      ].map((header, index) => (
                        <th
                          key={index}
                          className="p-3 text-sm font-semibold text-gray-600 text-center border-b-2 border-gray-200"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedData.map((data, index) => (
                      <tr key={data._id || index} className="hover:bg-gray-50 even:bg-gray-100">
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {(currentPage - 1) * itemsPerPage + index + 1}
                        </td>
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {data.clientId}
                        </td>
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {formatLabel(data.name)}
                        </td>
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {data.phone}
                        </td>
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {formatTableDateTime(data.createdAt)}
                        </td>
                        <td className="p-3 text-center border-b border-gray-200">
                          <div className="flex justify-center space-x-2">
                            <button
                              type='button'
                              className="px-2 sm:px-4 py-2 rounded-xl bg-blue-500 text-white hover:bg-blue-600 font-medium text-sm sm:text-base"
                              onClick={() => {
                                setDialog({
                                  open: true,
                                  type: "info",
                                  title: "View Details",
                                  message: "Want to view details for this client?",
                                  confirmText: "View",
                                  action: () => handleViewOpen(data._id),
                                })
                              }}
                            >
                              Details
                            </button>

                            <button
                              type="button"
                              data-menu-trigger="true"
                              className="p-2 rounded-full hover:bg-gray-100 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (menuState.open && menuState.rowId === data._id) {
                                  closeMenu();
                                  return;
                                }
                                const rect = e.currentTarget.getBoundingClientRect();
                                openMenu({
                                  id: data._id,
                                  row: data,
                                  position: {
                                    top: rect.bottom + window.scrollY - 140,
                                    left: rect.right + window.scrollX - 180,
                                  },
                                });
                              }}
                            >
                              <EllipsisVerticalIcon className="h-6 w-6 text-gray-600" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

          </div>

          {/* Pagination */}
          {searchMode === "quick" && totalPages > 1 && (
            <div className="w-full flex flex-col sm:flex-row justify-between items-center p-4 gap-2 sm:gap-4">
              {/* Showing Entries Text */}
              <span className="text-sm text-gray-600 text-center sm:text-left">
                Showing {startIndex} to {endIndex} of {totalBackendPg} entries
              </span>
              {/* Pagination Buttons */}
              <div className="flex flex-wrap justify-center sm:justify-end gap-1 sm:gap-2">
                {/* Previous Button */}
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={loaders.fetch || currentPage === 1}
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
                {/* Page Numbers */}
                {(() => {
                  const buttons = [];
                  const pageRange = 1; // current ±2 pages
                  let startPage = Math.max(currentPage - pageRange, 1);
                  let endPage = Math.min(currentPage + pageRange, totalPages);

                  if (startPage > 1) {
                    buttons.push(
                      <button
                        key={1}
                        onClick={() => setCurrentPage(1)}
                        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ${currentPage === 1 ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"}`}
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) buttons.push(<span key="start-ellipsis" className="px-2 hidden sm:inline">...</span>);
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    buttons.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ${currentPage === i ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"}`}
                      >
                        {i}
                      </button>
                    );
                  }

                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) buttons.push(<span key="end-ellipsis" className="px-2 hidden sm:inline">...</span>);
                    buttons.push(
                      <button
                        key={totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ${currentPage === totalPages ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"}`}
                      >
                        {totalPages}
                      </button>
                    );
                  }
                  return buttons;
                })()}
                {/* Next Button */}
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={loaders.fetch || currentPage === totalPages}
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default Client;